import { ProjectNode, ChecklistItem, ProjectData } from '@/shared/types/projectData';
import { getNodeAssignees } from '@/shared/utils/projectDataUtils';
import { buildKanbanViewState, normalizeKanbanColumnId, normalizeKanbanStatus } from '@/shared/utils/kanbanState';

/* ============================================================================
 * TASK DETAILS PANEL HELPER FUNCTIONS
 * Helper utilities for the TaskDetailsPanel component
 * ============================================================================ */

/**
 * Extract suggestions from project data for autocomplete fields
 * @param field - The field type to get suggestions for
 * @param currentValue - The current value being typed
 * @param projectData - The project data to extract suggestions from
 * @returns Array of suggestion strings
 */
export const getSuggestions = (
  field: string, 
  currentValue: string, 
  projectData: ProjectData | null
): string[] => {
  const nodes = projectData?.nodes || [];
  const uniqueValues = new Set<string>();

  nodes.forEach((node: ProjectNode) => {
    if (field === 'assignee') {
      // Get all assignees from both old and new format
      getNodeAssignees(node).forEach(assignee => uniqueValues.add(assignee));
    } else if (field === 'client' && node.projectClient) {
      uniqueValues.add(node.projectClient);
    } else if (field === 'tag' && node.tags) {
      node.tags.forEach((tag: string) => uniqueValues.add(tag));
    }
  });

  const suggestions = Array.from(uniqueValues);
  return suggestions.filter(s => 
    s.toLowerCase().includes(currentValue.toLowerCase()) && s !== currentValue
  );
};

/**
 * Initialize task with default values for new tasks
 * @param task - The partial task object to initialize
 * @returns Initialized task with default values
 */
export const initializeNewTask = (task: Partial<ProjectNode>): Partial<ProjectNode> => {
  const initialTask = { type: 'task' as const, ...task };
  
  // Add default checklist for new to-do tasks. A draft that already carries a
  // started or completed lifecycle (for example one opened from the In Progress
  // or Done column) keeps it: checklist evidence is authoritative, so an
  // unchecked default item would move the saved task back to To Do at 0%.
  if (
    !task.id &&
    initialTask.type === 'task' &&
    !initialTask.checklist &&
    normalizeKanbanStatus(initialTask.status) === 'todo'
  ) {
    initialTask.checklist = [{
      id: `checklist-${Date.now()}-default`,
      text: 'Complete this task',
      checked: false,
      order: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }];
    initialTask.progress = 0; // Start with 0% progress
    
    // Initialize status and completionState for new tasks
    if (!initialTask.status) {
      initialTask.status = 'todo';
    }
    if (!initialTask.completionState) {
      initialTask.completionState = 'not-started';
    }
    
    // Initialize viewState for new tasks
    if (!initialTask.viewState) {
      initialTask.viewState = buildKanbanViewState({}, 'todo', 0);
    } else if (initialTask.viewState.kanban?.columnId) {
      initialTask.viewState = buildKanbanViewState(
        initialTask.viewState,
        normalizeKanbanColumnId(initialTask.viewState.kanban.columnId),
        initialTask.viewState.kanban.order || 0
      );
    }
  }
  
  return initialTask;
};

/**
 * Calculate progress based on checklist items
 * @param checklist - The checklist items
 * @returns Progress percentage (0-100)
 */
export const calculateChecklistProgress = (checklist: ChecklistItem[] | undefined): number => {
  if (!checklist || checklist.length === 0) return 0;

  const completedItems = checklist.filter(item => item.checked).length;
  const totalItems = checklist.length;
  return Math.round((completedItems / totalItems) * 100);
};
